import { ChangeDetectionStrategy, Component, Signal, computed } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

interface IExtendedFile {
	fileReference: CosmosDb.ICosmosFileReference;
	buttons: IButton[];
	formattedFileSize: string;
}

interface IFileNameItem {
	name: string;
}

@Component({
	selector: 'mod-client-files.',
	templateUrl: './client-files.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientFilesComponent extends TypeCoreComponent<UserDb.IUserItem> {
	public readonly uploadFunction: (file: File) => Observable<CosmosDb.ICosmosFileReference[]> = (file) => {
		return this.dependencies.httpService
			.uploadCustomFile<CosmosDb.ICosmosFileReference>((builder) =>
				builder.withCustomApiRoute('user', 'uploadClientFile').withFile(file).build()
			)
			.pipe(
				map((response) => {
					return [response.data];
				})
			);
	};

	public readonly clientFiles: Signal<IExtendedFile[] | undefined> = computed(() => {
		const sharedData = this.sharedData();

		if (!sharedData?.data) {
			return undefined;
		}
		return sharedData?.data.clientFiles?.map((m) => this.mapToExtendedFile(m)) ?? [];
	});

	constructor() {
		super();
	}

	handleFilesChange(files: CosmosDb.ICosmosFileReference[] | undefined): void {
		this.saveFiles(files ?? []);
	}

	private openRenameFileDialog(file: CosmosDb.ICosmosFileReference): void {
		this.dependencies.dialogService.openFormDialog<IFileNameItem>({
			title: 'Set a name for the file',
			size: 'auto',
			fields: [
				{
					title: undefined,
					type: 'text',
					propertyName: 'name',
					required: true
				}
			],
			item: {
				name: file.originalFilename
			},
			save: (item) => {
				const sharedData = this.sharedData();

				if (!sharedData?.data) {
					return;
				}

				const galleryFile = sharedData.data.clientFiles?.find((m) => m.absoluteUrl === file.absoluteUrl);

				if (galleryFile) {
					galleryFile.originalFilename = item.name;
					this.saveUser('rename');
				}
			}
		});
	}

	private removeFile(file: CosmosDb.ICosmosFileReference): void {
		const sharedData = this.sharedData();

		if (!sharedData?.data) {
			return;
		}

		const clientFiles = (sharedData.data.clientFiles ?? []).filter((m) => m.absoluteUrl !== file.absoluteUrl);
		sharedData.data.clientFiles = clientFiles;
		this.saveUser('delete');
	}

	private mapToExtendedFile(fileReference: CosmosDb.ICosmosFileReference): IExtendedFile {
		const extendedFile: IExtendedFile = {
			formattedFileSize: unitConversionHelper.formatBytes(fileReference.sizeInBytes, 0),
			buttons: [
				{
					buttonSize: 'default',
					iconOnly: true,
					icon: 'edit',
					title: 'Edit',
					click: () => {
						this.openRenameFileDialog(fileReference);
					}
				},
				{
					buttonSize: 'default',
					iconOnly: true,
					icon: 'delete',
					confirm: {
						confirmMode: 'delete',
						text: `Are you sure to delete file '${fileReference.originalFilename}'?`
					},
					title: 'Delete',
					click: () => {
						this.removeFile(fileReference);
					}
				}
			],
			fileReference: fileReference
		};

		return extendedFile;
	}

	private saveFiles(files: CosmosDb.ICosmosFileReference[]): void {
		const sharedData = this.sharedData();

		if (!sharedData?.data) {
			return;
		}

		const clientFiles = sharedData.data.clientFiles ?? [];
		clientFiles.push(...files);
		sharedData.data.clientFiles = clientFiles;
		this.saveUser('upload');
	}

	private saveUser(action: 'delete' | 'upload' | 'rename'): void {
		const sharedData = this.sharedData();

		if (!sharedData?.data) {
			return;
		}

		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditUserFilesRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editUserFiles')
						.withData({
							files: sharedData.data.clientFiles ?? []
						})
						.withId(sharedData.data.id)
						.build()
				)
				.pipe(
					map((response) => {
						super.updateSharedData({
							data: response.data,
							linkedItems: response.linkedItems
						});

						if (action === 'delete') {
							super.notifyDeleted('File');
						} else if (action === 'upload') {
							super.notifyUploaded();
						} else if (action === 'rename') {
							super.notifySaved('File');
						}
					})
				)
		);
	}
}
